import { Text, Button } from '@msc-b2c/shared-components';
import { VoyagersClubJoinMscVoyagerClubProps } from '@msc-b2c/interfaces/types';
import styles from './voyagersclubjoinmscvoyagerclubLayout.module.scss';
import { useState } from 'react';
import {
  closeLoader,
  getActiveSession,
  openLoader,
  UpdateVoyagerClubInfoMyMsc,
  getCountryCode,
  setErrorManagerCode,
} from '@msc-b2c/shared-utils';
import { useDispatch } from 'react-redux';

export function VoyagersClubJoinMscVoyagerClubLayout({
  membershipBenefitsTitle,
  membershipBenefitsSubtitle,
  benefitsListTitleAndLists,
  registerConsentText,
  joinButtonLabel,
  joinButtonLinkButtonUrl,
  joinButtonStyle,
  joinButtonSize,
}: Readonly<VoyagersClubJoinMscVoyagerClubProps>) {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const onJoinClick = async () => {
    setIsLoading(true);
    openLoader('.loader');
    try {
      const session = getActiveSession();
      const email = session?.email;
      if (!email) {
        setIsLoading(false);
        closeLoader('.loader');
        return;
      }
      const countryCode = getCountryCode();
      if (!countryCode) {
        setIsLoading(false);
        closeLoader('.loader');
        return;
      }
      const requestData = {
        address: { countryCode },
        thirdPartyConsent: false,
        profilingConsent: false,
        marketingConsents: { email: false, mobilePhone: false },
      };
      const urlParams = { email };
      const api = new UpdateVoyagerClubInfoMyMsc(false, false, requestData, urlParams);
      try {
        await api.sendData();
        if (joinButtonLinkButtonUrl?.trim()) {
          setIsLoading(false);
          globalThis.location.assign(joinButtonLinkButtonUrl);
          return;
        }
      } catch (error: any) {
        dispatch(setErrorManagerCode(error?.issue_code ?? 'unknown_error'));
      }
    } catch (err: any) {
      dispatch(setErrorManagerCode(err?.issue_code ?? 'unknown_error'));
    } finally {
      setIsLoading(false);
      closeLoader('.loader');
    }
  };

  // Parse benefitsListTitleAndLists to extract title and list items
  // If it's HTML, parse it; if it's plain text, split by lines
  let benefitsTitle = '';
  let benefitsItems: string[] = [];
  if (benefitsListTitleAndLists) {
    // Try to parse as HTML list
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = benefitsListTitleAndLists;
    const ul = tempDiv.querySelector('ul');
    if (ul) {
      benefitsItems = Array.from(ul.querySelectorAll('li')).map((li) => li.textContent || '');
      // Try to get title from preceding element
      const prev = ul.previousElementSibling;
      if (prev?.textContent) benefitsTitle = prev.textContent;
    } else {
      // Fallback: split by lines
      const lines = benefitsListTitleAndLists.split('\n');
      benefitsTitle = lines[0];
      benefitsItems = lines.slice(1).filter((line) => line.trim());
    }
  }

  return (
    <div className={styles['cmp-not-inscribed-layout']}>
      <div className={styles['cmp-not-inscribed-layout__header-container']}>
        <div className={styles['cmp-not-inscribed-layout__card-header']}>
          <Text
            text={membershipBenefitsTitle ?? ''}
            tagHtml="span"
            styleOption="cmp-not-inscribed-layout__text_title header-m"
          />
        </div>

        {membershipBenefitsSubtitle && (
          <div className={styles['cmp-not-inscribed-layout__card-body']}>
            <Text
              text={membershipBenefitsSubtitle ?? ''}
              styleOption="cmp-not-inscribed-layout__text"
            />
          </div>
        )}

        {benefitsListTitleAndLists && (
          <div className={styles['cmp-not-inscribed-layout__list-of-items']}>
            {benefitsTitle && (
              <Text
                text={benefitsTitle}
                styleOption="cmp-not-inscribed-layout__text body-big"
              />
            )}
            <ul className="cmp-not-inscribed-layout__text">
              {benefitsItems.map((item, idx) => (
                <li key={item}>
                  <span className="checkmark">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {registerConsentText && (
          <div className={styles['cmp-not-inscribed-layout__consent']}>
            <Text
              text={registerConsentText ?? ''}
              styleOption="cmp-not-inscribed-layout__text body-big"
            />
          </div>
        )}

        <div className={styles['cmp-not-inscribed-layout__button-wrapper']}>
          <Button
            text={joinButtonLabel ?? ''}
            onClick={onJoinClick}
            styleOption={`${joinButtonStyle} ${joinButtonSize}`}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
}

export default VoyagersClubJoinMscVoyagerClubLayout;
